import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GradientBackground } from "@/components";
import { CodeForm, CodeTitle } from "@/components/code";
import { strings } from "@/constants";
import {
    CodeLayoutVariantEnum,
    CodePageStatusEnum,
    GradientVariantEnum,
    LanguageEnum,
} from "@/enums";
import { useAuth, useExamPolling } from "@/hooks";
import type { ChatResultInterface } from "@/interfaces";
import { CodeLayout } from "@/layouts";
import type { CodePageStatusType, LanguageType } from "@/types";
import { formatRecommendedJobsToMarkdownHandler } from "@/utils";

const mapStatusHandler = (status: ReturnType<typeof useExamPolling>["status"]): CodePageStatusType => {
    if (status === "loading") return CodePageStatusEnum.LOADING;

    if (status === "error") return CodePageStatusEnum.ERROR;

    if (status === "success") return CodePageStatusEnum.SUCCESS;

    return CodePageStatusEnum.IDLE;
};

export const CodePage = () => {
    const [code, setCode] = useState("");

    const [lang, setLang] = useState<LanguageType>(LanguageEnum.EN);

    const navigate = useNavigate();

    const { user } = useAuth();

    const {
        errorMessage,
        onProcess: runProcessHandler,
        onReset: resetPollingHandler,
        progress,
        result,
        status: pollingStatus,
    } = useExamPolling();

    const codeLength = 8;

    const isCodeValid = code.trim().length === codeLength;

    const status = mapStatusHandler(pollingStatus);

    const layoutVariant = status === CodePageStatusEnum.ERROR ? CodeLayoutVariantEnum.ERROR : CodeLayoutVariantEnum.DEFAULT;

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isCodeValid) return;

        await runProcessHandler({
            code,
            lang,
        });
    };

    const resetHandler = () => {
        resetPollingHandler();

        setCode("");
    };

    useEffect(
        () => {
            if (status !== CodePageStatusEnum.SUCCESS || !result) return;

            const chatResult: ChatResultInterface = {
                code: result.code,
                content: formatRecommendedJobsToMarkdownHandler(result.recommendedJobs),
                id: crypto.randomUUID(),
                recommendedJobs: result.recommendedJobs,
                userName: user?.name || strings.code.defaultUserName,
            };

            const resultUrl = result.lang === LanguageEnum.AR ? `/result?jobId=${result.jobId}&code=${encodeURIComponent(result.code)}&lang=${LanguageEnum.AR}` : `/result?jobId=${result.jobId}&code=${encodeURIComponent(result.code)}`;

            navigate(
                resultUrl,
                { state: { result: chatResult } },
            );
        },
        [
            status,
            result,
            user,
            navigate,
        ],
    );

    return (
        <CodeLayout variant={layoutVariant}>
            {(status === CodePageStatusEnum.LOADING || status === CodePageStatusEnum.ERROR) && <GradientBackground variant={status === CodePageStatusEnum.LOADING ? GradientVariantEnum.LOADING : GradientVariantEnum.ERROR} />}
            <div
                className="
                    w-full
                    max-w-2xl
                    px-4
                    text-center
                    sm:px-8
                "
            >
                <CodeTitle
                    errorMessage={errorMessage}
                    progress={progress}
                    status={status}
                />
                <CodeForm
                    code={code}
                    isCodeValid={isCodeValid}
                    lang={lang}
                    status={status}
                    onChangeCode={setCode}
                    onChangeLang={setLang}
                    onReset={resetHandler}
                    onSubmit={submitHandler}
                />
            </div>
        </CodeLayout>
    );
};
